import axiosInstance from '@/services/axios';

/**
 * Ads are stored under `public/build/storage/advertisments/` (URL `/build/storage/advertisments/...`).
 */
export function backendOrigin() {
  const site = (process.env.VUE_APP_SITE_URL || '').trim().replace(/\/$/, '');
  if (site) return site;
  const fromAxios = (axiosInstance.defaults.baseURL || '').trim().replace(/\/$/, '');
  if (fromAxios.endsWith('/api')) return fromAxios.slice(0, -4);
  if (fromAxios) return fromAxios;
  const api = (process.env.VUE_APP_API_URL || '').trim().replace(/\/$/, '');
  if (api.endsWith('/api')) return api.slice(0, -4);
  return api;
}

/**
 * Origin of the Laravel app (same host as the API). Prefer axios `baseURL` so images
 * always match the server that stores files — avoids broken `<img>` when the Vue dev
 * proxy for `/build/storage` fails or `VUE_APP_SITE_URL` is wrong.
 */
export function mediaBackendOrigin() {
  const fromAxios = (axiosInstance.defaults.baseURL || '').trim().replace(/\/$/, '');
  if (fromAxios.endsWith('/api')) {
    const base = fromAxios.slice(0, -4);
    if (base) return base;
  } else if (fromAxios && /^https?:\/\//i.test(fromAxios)) {
    return fromAxios;
  }
  const site = (process.env.VUE_APP_SITE_URL || '').trim().replace(/\/$/, '');
  if (site) return site;
  const api = (process.env.VUE_APP_API_URL || '').trim().replace(/\/$/, '');
  if (api.endsWith('/api')) {
    const b = api.slice(0, -4);
    if (b) return b;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

/** Turn `/storage/...` or `/build/storage/...` into a full URL using the API server origin. */
export function withAbsoluteMediaOrigin(href) {
  if (!href || typeof href !== 'string') return '';
  const u = href.trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/storage/') || u.startsWith('/build/storage/')) {
    const origin = mediaBackendOrigin();
    if (origin) return `${origin}${u}`;
  }
  return u;
}

/**
 * Turn any Laravel `/storage/...` URL into one served from `backendOrigin()`.
 * Fixes broken images when API `image_url` was built with a wrong `APP_URL`
 * (e.g. localhost in .env while the shop uses production).
 */
export function resolveAdImageUrl(href) {
  if (!href || typeof href !== 'string') return '';
  const u = href.trim();
  const origin = backendOrigin();

  if (/^https?:\/\//i.test(u)) {
    try {
      const parsed = new URL(u);
      let pathWithQuery = `${parsed.pathname}${parsed.search || ''}`;

      // Some Laravel setups may generate URLs like //storage/...
      // Normalize them so our checks work and we can safely rewrite hosts.
      pathWithQuery = pathWithQuery.replace(/^\/{2,}storage\//i, '/storage/');

      if (
        pathWithQuery.startsWith('/storage/') ||
        pathWithQuery.startsWith('/build/storage/')
      ) {
        // Always use a root-relative path so:
        // - Vue dev server (:8080) can proxy `/storage` and `/build/storage` to Laravel
        // - `VUE_APP_SITE_URL` / production domains cannot mismatch `APP_URL` in image URLs
        return pathWithQuery;
      }
    } catch {
      return u;
    }
    return u;
  }

  if (u.startsWith('/')) {
    // For same-origin endpoints (like `/api/...`) keep as-is.
    // This prevents accidentally rewriting to a misconfigured localhost backend origin.
    if (u.startsWith('/api/') || u.startsWith('/storage/') || u.startsWith('/build/storage/'))
      return u;
    if (origin) return `${origin}${u}`;
  }
  return u;
}

/** Matches Laravel `config('media.web_prefix')` + `/advertisments/`. */
const AD_STORAGE_PREFIX = '/build/storage/advertisments/';

function normalizeAddType(value) {
  const asNumber = Number(value);
  if (Number.isFinite(asNumber)) return asNumber;
  if (value == null) return value;

  const text = String(value).trim().toLowerCase();
  if (!text) return value;

  // Fallback mapping when backend returns label text instead of numeric type.
  if (text.includes('top left')) return 1;
  if (text.includes('top center') || text.includes('top middle')) return 2;
  if (text.includes('top right')) return 3;
  if (text.includes('middle left')) return 4;
  if (text.includes('middle right')) return 5;
  if (text.includes('bottom left')) return 6;
  if (text.includes('bottom middle top') || text.includes('middle top')) return 7;
  if (text.includes('bottom middle') || text.includes('middle bottom')) return 8;
  if (text.includes('bottom right')) return 9;

  return value;
}

export function imageUrlFromAdRecord(ad) {
  if (!ad || typeof ad !== 'object') return '';
  const direct = ad.image_url || ad.imageUrl;
  if (direct && String(direct).trim()) {
    return resolveAdImageUrl(String(direct).trim());
  }
  const name = ad.image;
  if (name == null || String(name).trim() === '') return '';
  const basename = String(name).trim().replace(/^\/+/, '').split('/').pop();
  if (!basename) return '';
  const safe = encodeURIComponent(basename);
  // Root-relative so dev-server proxy and same-host production both resolve correctly.
  return `${AD_STORAGE_PREFIX}${safe}`;
}

export function normalizeAdRecord(ad) {
  if (!ad || typeof ad !== 'object') return ad;
  const addType = normalizeAddType(ad.add_type);
  return {
    ...ad,
    add_type: addType,
    image_url: withAbsoluteMediaOrigin(imageUrlFromAdRecord(ad)),
  };
}

export function normalizeAdsList(list) {
  if (!Array.isArray(list)) return [];
  return list.map((a) => normalizeAdRecord(a)).filter(Boolean);
}

export function adIsExternal(ad) {
  const u = ad && ad.url != null ? String(ad.url).trim() : '';
  return /^https?:\/\//i.test(u);
}

export function adRouterPath(ad) {
  const u = ad && ad.url != null ? String(ad.url).trim() : '';
  if (!u || adIsExternal(ad)) return '/shop';
  if (u.startsWith('/')) return u;
  return `/${u}`;
}

export function adExternalHref(ad) {
  return ad && ad.url != null ? String(ad.url).trim() : '';
}